import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useEventLogging } from '../../../../models/Logging';
import { FileSendLinkPlaceholder } from '../../../../models/Token';
import { useAddTextingStepAttachmentsMutation } from '../../../../queries/Automation/mutations/attachments/useAddTextingStepAttachmentsMutation';
import { useRemoveTextingStepAttachmentMutation } from '../../../../queries/Automation/mutations/attachments/useRemoveTextingStepAttachmentMutation';
import { Button } from '../../../components-v2/Button';
import { Checkbox } from '../../../components-v2/Checkbox';
import { LoadingSpinner } from '../../LoadingSpinner';
import { styleSheet } from './styles';

const TOGGLE_ID = 'texting-attachments-send-as-link';

interface TextingStepAttachmentsProps {
	step: Api.ITextingAutomationStep;
	templateId: string;
	stepId: string;
	readonly: boolean;
	canSendFileLinks: boolean;
	contentHasFileLinkToken: boolean;
	impersonationContext?: Api.IImpersonationContext;
	onStepChanged?: (updatedStep: Api.ITextingAutomationStep) => void;
	onHasFileLinkToken?: () => boolean;
	onInsertMergeField?: (symbol: string) => string | undefined;
	onRemoveMergeField?: (symbols: string | string[]) => string | undefined;
	onSave?: (sendAttachmentsAsLink: boolean, newContent?: string) => void;
}

export function TextingStepAttachments({
	step,
	templateId,
	stepId,
	readonly,
	canSendFileLinks,
	contentHasFileLinkToken,
	impersonationContext,
	onStepChanged,
	onHasFileLinkToken,
	onInsertMergeField,
	onRemoveMergeField,
	onSave,
}: TextingStepAttachmentsProps): React.ReactElement | null {
	const [localAttachments, setLocalAttachments] = React.useState<Api.IFileAttachment[]>(step.attachments ?? []);
	const fileInputRef = React.useRef<HTMLInputElement>(null);
	const [removingIds, setRemovingIds] = React.useState<Set<string>>(new Set());
	const { logApiError } = useEventLogging('TextingStepAttachments');

	React.useEffect(() => {
		if (step.attachments !== undefined) {
			setLocalAttachments(step.attachments);
		}
	}, [step.attachments]);

	const addMutation = useAddTextingStepAttachmentsMutation({
		onSuccess: data => {
			setLocalAttachments(prev => data.attachments ?? prev);
			onStepChanged?.(data);
		},
		onError: error => {
			logApiError('AddTextingAttachment-Error', error);
			toast.error('Failed to attach file.');
		},
	});

	const removeMutation = useRemoveTextingStepAttachmentMutation({
		onSuccess: (data, variables) => {
			const next = data?.attachments ?? localAttachments.filter(a => a.id !== variables.attachmentId);
			setLocalAttachments(next);
			let updatedStep = data ?? ({ ...step, attachments: next } as Api.ITextingAutomationStep);

			if (next.length === 0 && updatedStep.sendAttachmentsAsLink) {
				updatedStep = { ...updatedStep, sendAttachmentsAsLink: undefined };
				const allSymbols = [FileSendLinkPlaceholder.symbol, ...(FileSendLinkPlaceholder.additionalSymbols ?? [])];
				const newContent = onRemoveMergeField?.(allSymbols);
				void onSave?.(false, newContent);
			}

			onStepChanged?.(updatedStep);
		},
		onError: error => {
			logApiError('RemoveTextingAttachment-Error', error);
			toast.error('Failed to remove attachment.');
		},
	});

	const totalAttachmentSize = localAttachments.reduce((sum, a) => sum + a.fileSize, 0);
	const isTotalSizeLarge = totalAttachmentSize > Api.TextMessagingMaxFileByteSize;

	React.useEffect(() => {
		if (isTotalSizeLarge && !step.sendAttachmentsAsLink && canSendFileLinks && !readonly) {
			onStepChanged?.({ ...step, sendAttachmentsAsLink: true });
			const hasToken = onHasFileLinkToken?.() ?? contentHasFileLinkToken;
			const newContent = !hasToken ? onInsertMergeField?.(FileSendLinkPlaceholder.symbol) : undefined;
			void onSave?.(true, newContent);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isTotalSizeLarge]);

	if (localAttachments.length === 0) {
		if (!contentHasFileLinkToken) return null;
		return (
			<div className={css(styleSheet.container)}>
				<p className={css(styleSheet.warningText)}>
					Attach a file or remove the file download link merge field from the message.
				</p>
			</div>
		);
	}

	const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = Array.from(e.target.files ?? []);
		if (files.length === 0) return;

		const currentTotal = localAttachments.reduce((sum, a) => sum + a.fileSize, 0);
		const newFilesTotal = files.reduce((sum, f) => sum + f.size, 0);
		if (currentTotal + newFilesTotal > Api.TextMessagingMaxLinkFileByteSize) {
			toast.error('Total attachment size cannot exceed 20 MB.');
			if (fileInputRef.current) fileInputRef.current.value = '';
			return;
		}

		try {
			await addMutation.mutateAsync({ templateId, stepId, attachmentFiles: files, impersonationContext });
		} catch {
			// error handled in onError above
		}
		if (fileInputRef.current) fileInputRef.current.value = '';
	};

	const handleRemove = async (attachmentId: string) => {
		setRemovingIds(prev => new Set(prev).add(attachmentId));
		try {
			await removeMutation.mutateAsync({ templateId, stepId, attachmentId, impersonationContext });
		} catch {
			// error handled in onError above
		} finally {
			setRemovingIds(prev => {
				const next = new Set(prev);
				next.delete(attachmentId);
				return next;
			});
		}
	};

	const handleToggle = (value: boolean) => {
		onStepChanged?.({ ...step, sendAttachmentsAsLink: value || undefined });
		if (value) {
			const hasToken = onHasFileLinkToken?.() ?? contentHasFileLinkToken;
			const newContent = !hasToken ? onInsertMergeField?.(FileSendLinkPlaceholder.symbol) : undefined;
			void onSave?.(true, newContent);
		} else {
			const allSymbols = [FileSendLinkPlaceholder.symbol, ...(FileSendLinkPlaceholder.additionalSymbols ?? [])];
			const newContent = onRemoveMergeField?.(allSymbols);
			void onSave?.(false, newContent);
		}
	};

	const showToggle = !readonly && canSendFileLinks && localAttachments.length > 0;
	const showWarning = !!step.sendAttachmentsAsLink && !contentHasFileLinkToken;

	return (
		<div className={css(styleSheet.container)}>
			<span className={css(styleSheet.headerLabel)}>
				{localAttachments.length} attachment{localAttachments.length !== 1 ? 's' : ''}
			</span>
			{showToggle && (
				<div className={css(styleSheet.toggleRow)}>
					<Checkbox
						checked={isTotalSizeLarge || !!step.sendAttachmentsAsLink}
						disabled={isTotalSizeLarge}
						id={TOGGLE_ID}
						onCheckedChange={handleToggle}
					/>
					<label className={css(styleSheet.toggleLabel)} htmlFor={TOGGLE_ID}>
						{isTotalSizeLarge ? 'Send as download link (required for large files)' : 'Send as download link'}
					</label>
				</div>
			)}
			<div className={css(styleSheet.attachmentList)}>
				{localAttachments.map(att => (
					<div key={att.id} className={css(styleSheet.attachmentItem)}>
						<span>{att.fileName}</span>
						{!readonly && (
							<Button
								aria-label={`Remove ${att.fileName}`}
								variant='ghost'
								size='icon'
								styles={[styleSheet.removeButton]}
								disabled={removingIds.has(att.id)}
								onClick={() => handleRemove(att.id)}
							>
								✕
							</Button>
						)}
					</div>
				))}
			</div>
			{addMutation.isPending && (
				<div className={css(styleSheet.uploadingRow)}>
					<LoadingSpinner type='extra-tiny' />
					<span>Uploading… this may take a moment</span>
				</div>
			)}
			{!readonly && (
				<>
					<Button
						variant='link'
						size='sm'
						styles={[styleSheet.addButton]}
						disabled={addMutation.isPending}
						onClick={() => fileInputRef.current?.click()}
					>
						Attach file
					</Button>
					<input
						ref={fileInputRef}
						type='file'
						multiple
						style={{ display: 'none' }}
						disabled={addMutation.isPending}
						onChange={handleFileChange}
					/>
				</>
			)}
			{showWarning && <p className={css(styleSheet.warningText)}>Add a file link merge field to the message body.</p>}
			{!!step.sendAttachmentsAsLink && contentHasFileLinkToken && (
				<p className={css(styleSheet.infoText)}>Files will be replaced with a secure download link in the message.</p>
			)}
		</div>
	);
}
